<script setup>
import { useFloating, autoUpdate, limitShift, offset, shift, flip, arrow } from '@floating-ui/vue'

const props = defineProps({
  propRef: {
    type: Object,
    default: () => ({})
  },
  cardRef: {
    type: Object,
    default: () => ({})
  }
})
const { propRef } = toRefs(props)

const popoverRef = ref(propRef)
const popoverArrowRef = ref(null)
const popoverContentRef = ref(null)
const mobileOverlayRef = ref(null)

const { floatingStyles: popoverStyles, middlewareData, placement } = useFloating(popoverRef, popoverContentRef, {
  placement: 'right',
  whileElementsMounted: autoUpdate,
  middleware: [
    offset({
      mainAxis: 36,
      crossAxis: 150
    }),
    flip({ padding: 70 }),
    shift({
      padding: 5,
      limiter: limitShift({
        offset: 100
      })
    }),
    arrow({
      element: popoverArrowRef,
      padding: 40
    })
  ]
})

const { elementX, elementY, elementHeight, elementWidth } = useMouseInElement(popoverRef)
const isInsideIcon = computed(() => (elementY.value > 0 && elementY.value <= elementHeight.value) && (elementX.value > 0 && elementX.value <= elementWidth.value))

const { elementX: pElementX, elementY: popElementY, elementHeight: popElementHeight, elementWidth: popElementWidth } = useMouseInElement(popoverContentRef)
 // consider mainAxis offset, otherwise elementX will never be > 0
const popElementX = computed(() => placement.value === 'right' ? pElementX.value + 50 : pElementX.value - 50)
const isInsidePopover = computed(() => (popElementY.value > 0 && popElementY.value <= popElementHeight.value) && (popElementX.value > 0 && popElementX.value <= popElementWidth.value))

const isOutside = computed(() => !isInsideIcon.value && !isInsidePopover.value)

const { isDesktopView, isMobileView } = useScreen()

const isMobileOpen = ref(false)
const isOpen = computed(() => isMobileOpen.value)

const open = () => {
  if (!isMobileOpen.value && !isOutside.value) isMobileOpen.value = true
}

const close = () => {
  if (isMobileOpen.value && isOutside.value) isMobileOpen.value = false
}

const arrowPlacement = computed(() => placement.value === 'left' ? 'right' : 'left')

const popoverContainerRef = ref(null)

defineExpose({
  open,
  close
})
</script>

<template>
  <div ref="popoverContainerRef" class="relative" :class="isMobileView && isOpen ? 'z-[30]' : isDesktopView && !isOutside ? 'z-[11]' : 'z-[10]'">
    <slot name="reference" v-bind="{ isMobileView, isDesktopView, open, close, isOutside }" />

    <Transition name="fade">
      <div 
        v-if="isMobileView && isOpen"
        ref="mobileOverlayRef"
        class="fixed top-0 left-0 h-dvh w-dvw bg-slate-900/20 backdrop-blur-[2px] z-[299]"
        @click.self="close()"
      />
    </Transition>

    <Transition name="fade">
      <div
        v-if="isMobileView && isOpen || isDesktopView && !isOutside"
        ref="popoverContentRef"
        class="fixed top-1/2 -translate-y-[50%] left-1/2 -translate-x-[50%] mx-auto z-[301] w-[90%] lg:w-full max-w-[423px] min-h-[150px] max-h-[80dvh] rounded-[40px] bg-darkBlue text-white"
        :style="isDesktopView ? popoverStyles : null"
      >
        <button v-if="isMobileView" class="absolute right-0 -top-16 p-4 text-2xl bg-slate-900 transition-all active:bg-slate-950 rounded-full text-white" @click="close()">
          <IconCloseButton class="size-5" />
        </button>

        <div
          ref="popoverArrowRef"
          class="absolute size-[60px]"
          :style="{
            [arrowPlacement]:
              middlewareData.arrow?.x != null
                ? `${middlewareData.arrow.x - popoverArrowRef.offsetWidth}px` 
                  : popoverArrowRef ? `-${popoverArrowRef?.offsetWidth}px` : '',
            top:
              middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : '',
          }"
        >
          <div
            v-if="isDesktopView"
            class="border-[30px] border-transparent z-[300]"
            :class="[ arrowPlacement === 'left' ? 'border-r-darkBlue' : 'border-l-darkBlue' ]"
          />
        </div>

        <div tabindex="0" class="p-6 text-pretty">
          <!-- Content -->
          <slot />
        </div>

      </div>
    </Transition>
  </div>
</template>